import React, { useState } from 'react';

import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import { useDismissViolationMutation } from '../../../../redux/apis/attendanceViolationApi';
import {
  canApplyLeaveForViolation,
  getViolationActionConfig,
  getViolationActionShortLabel,
  getViolationId
} from '../../../../utils/attendanceViolationHelpers';
import { notification } from '../../../../utils/notification';
import { TOAST_TYPE } from '../../../../utils/constant/errorMessage';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const ActionLabel = ({ violation }) => {
  const config = getViolationActionConfig(violation);
  const shortLabel = getViolationActionShortLabel(violation);

  if (!shortLabel || shortLabel.toLowerCase() === 'hr notified') {
    return null;
  }

  return (
    <span className={`att-violation-status-pill att-violation-status-pill--${config.color}`}>
      {shortLabel}
    </span>
  );
};

const ViolationRowActions = ({ violation, summary, onApplyLeave, onActionSuccess }) => {
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [dismissViolation, { isLoading: dismissing }] = useDismissViolationMutation();

  const violationId = getViolationId(violation);
  const showApplyLeave = canApplyLeaveForViolation(summary, violation);
  const showDismiss = showApplyLeave && Boolean(violationId);

  if (violation?.leaveApplied) {
    return (
      <span
        className="att-violation-row-actions att-violation-row-actions--done"
        title="Leave applied"
      >
        <i className="bi bi-check-circle-fill" aria-hidden />
      </span>
    );
  }

  if (!showApplyLeave) {
    return <ActionLabel violation={violation} />;
  }

  const handleDismiss = async () => {
    if (!violationId) {
      notification('Violation id is missing.', TOAST_ERROR);
      return;
    }

    try {
      const res = await dismissViolation(violationId);
      if (res?.error) {
        notification(
          res?.error?.data?.statusMessage || 'Failed to dismiss violation.',
          TOAST_ERROR
        );
        return;
      }

      notification(res?.data?.statusMessage || 'Violation dismissed.', TOAST_SUCCESS);
      setShowDismissConfirm(false);
      onActionSuccess?.();
    } catch (error) {
      notification(error?.message || 'Failed to dismiss violation.', TOAST_ERROR);
    }
  };

  return (
    <>
      <div className="att-violation-row-actions">
        <ActionLabel violation={violation} />
        {showDismiss ? (
          <button
            type="button"
            className="att-violation-row-actions__btn att-violation-row-actions__btn--dismiss"
            title="Dismiss violation"
            aria-label="Dismiss violation"
            disabled={dismissing}
            onClick={() => setShowDismissConfirm(true)}
          >
            <i className="bi bi-x" aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          className="att-violation-row-actions__btn att-violation-row-actions__btn--approve"
          title="Apply leave"
          aria-label="Apply leave"
          onClick={onApplyLeave}
        >
          <i className="bi bi-check" aria-hidden />
        </button>
      </div>

      <ConfirmDialog
        show={showDismissConfirm}
        onHide={() => setShowDismissConfirm(false)}
        onConfirm={handleDismiss}
        title="Dismiss violation?"
        message="This will permanently remove the violation log. This action cannot be undone."
        confirmLabel="Dismiss"
        cancelLabel="Cancel"
        variant="danger"
        loading={dismissing}
      />
    </>
  );
};

export default ViolationRowActions;
